import React, { useEffect, useMemo, useState } from 'react';
import { Box, Flex, Grid, Heading, IconButton, SimpleGrid, Text, Tooltip } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { CheckCircle, Info, LinkSimple, Warning, WarningCircle } from 'phosphor-react';

import { GlassCard } from '../../../../../components/glass';

/* ---------- Prose (texto de leitura) ---------- */

export function Paragrafo({ children }: { children: React.ReactNode }) {
  return (
    <Text fontSize="16px" lineHeight="1.7" color="#334155" mb={4} maxW="68ch">
      {children}
    </Text>
  );
}

export function Forte({ children }: { children: React.ReactNode }) {
  return (
    <Text as="strong" fontWeight="700" color="#0F172A">
      {children}
    </Text>
  );
}

export function SubTitulo({ children }: { children: React.ReactNode }) {
  return (
    <Heading
      as="h3"
      fontSize="15px"
      fontWeight="700"
      color="#0F172A"
      letterSpacing="-0.01em"
      mt={6}
      mb={2}
    >
      {children}
    </Heading>
  );
}

/* ---------- Termo do glossário (tooltip inline) ---------- */

export function Termo({ def, children }: { def: string; children: React.ReactNode }) {
  return (
    <Tooltip
      label={def}
      hasArrow
      bg="#0F172A"
      color="white"
      borderRadius="lg"
      px={3}
      py={2}
      fontSize="12px"
      maxW="260px"
    >
      <Text
        as="span"
        tabIndex={0}
        borderBottom="1px dashed #94A3B8"
        cursor="help"
        sx={{ textUnderlineOffset: '3px' }}
      >
        {children}
      </Text>
    </Tooltip>
  );
}

/* ---------- Callout (estilo Obsidian em Chakra) ---------- */

type CalloutTone = 'info' | 'sucesso' | 'aviso' | 'perigo';

const CALLOUT: Record<
  CalloutTone,
  { bg: string; fg: string; bar: string; icon: React.ReactNode; rotulo: string }
> = {
  info: {
    bg: 'rgba(99, 102, 241, 0.08)',
    fg: '#4338CA',
    bar: '#6366F1',
    icon: <Info size={16} weight="fill" />,
    rotulo: 'Conceito',
  },
  sucesso: {
    bg: 'rgba(16, 185, 129, 0.10)',
    fg: '#059669',
    bar: '#10B981',
    icon: <CheckCircle size={16} weight="fill" />,
    rotulo: 'Boa prática',
  },
  aviso: {
    bg: 'rgba(245, 158, 11, 0.12)',
    fg: '#B45309',
    bar: '#F59E0B',
    icon: <Warning size={16} weight="fill" />,
    rotulo: 'Atenção',
  },
  perigo: {
    bg: 'rgba(239, 68, 68, 0.10)',
    fg: '#DC2626',
    bar: '#EF4444',
    icon: <WarningCircle size={16} weight="fill" />,
    rotulo: 'Cuidado',
  },
};

export function Callout({
  tone = 'info',
  titulo,
  children,
}: {
  tone?: CalloutTone;
  titulo?: string;
  children: React.ReactNode;
}) {
  const c = CALLOUT[tone];
  return (
    <Flex
      gap={2}
      bg={c.bg}
      borderRadius="xl"
      p={4}
      my={5}
      maxW="68ch"
      sx={{ boxShadow: `inset 3px 0 0 0 ${c.bar}` }}
    >
      <Box color={c.fg} mt="2px" flexShrink={0}>
        {c.icon}
      </Box>
      <Box>
        <Text fontSize="11px" fontWeight="700" textTransform="uppercase" color={c.fg} mb={0.5}>
          {titulo ?? c.rotulo}
        </Text>
        <Box fontSize="13px" color="#334155" lineHeight="1.6">
          {children}
        </Box>
      </Box>
    </Flex>
  );
}

/* ---------- Exemplo trabalhado ---------- */

export function ExemploTrabalhado({
  titulo,
  children,
}: {
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      borderWidth="1px"
      borderColor="rgba(99, 102, 241, 0.25)"
      bg="rgba(99, 102, 241, 0.03)"
      borderRadius="xl"
      p={4}
      my={5}
      maxW="68ch"
    >
      <Text fontSize="10px" fontWeight="700" color="#6366F1" textTransform="uppercase" mb={2}>
        Exemplo · {titulo}
      </Text>
      <Box fontSize="14px" color="#334155" lineHeight="1.6">
        {children}
      </Box>
    </Box>
  );
}

/* ---------- Mini-KPIs (placar, não clicável) ---------- */

export interface MiniKpi {
  label: string;
  valor: string;
  icon: React.ReactNode;
  accent: string;
  sub?: string;
}

export function MiniKpiRow({ kpis }: { kpis: MiniKpi[] }) {
  return (
    <SimpleGrid columns={{ base: 2, md: kpis.length > 3 ? 4 : kpis.length }} spacing={4} my={5}>
      {kpis.map(k => (
        <GlassCard key={k.label} intensity="strong" p={4}>
          <Flex align="center" justify="space-between" mb={1}>
            <Text fontSize="10px" fontWeight="600" color="#94A3B8" textTransform="uppercase">
              {k.label}
            </Text>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="28px"
              h="28px"
              borderRadius="lg"
              bg={`${k.accent}14`}
              color={k.accent}
            >
              {k.icon}
            </Box>
          </Flex>
          <Text
            fontSize={{ base: '20px', md: '24px' }}
            fontWeight="700"
            color="#0F172A"
            lineHeight="1.1"
          >
            {k.valor}
          </Text>
          {k.sub && (
            <Text fontSize="11px" color="#64748B" fontWeight="500" mt={0.5}>
              {k.sub}
            </Text>
          )}
        </GlassCard>
      ))}
    </SimpleGrid>
  );
}

/* ---------- Seção de ensino (âncora + header numerado) ---------- */

export function SecaoEnsino({
  id,
  numero,
  titulo,
  children,
}: {
  id: string;
  numero?: number;
  titulo: string;
  children: React.ReactNode;
}) {
  return (
    <GlassCard
      as="section"
      id={id}
      intensity="medium"
      p={{ base: 6, md: 8 }}
      mb={{ base: 8, md: 12 }}
      sx={{ scrollMarginTop: '90px' }}
    >
      <Flex align="baseline" gap={2} mb={4}>
        {numero != null && (
          <Text fontSize="13px" fontWeight="800" color="#6366F1">
            §{numero}
          </Text>
        )}
        <Heading
          as="h2"
          fontSize={{ base: '19px', md: '22px' }}
          fontWeight="700"
          color="#0F172A"
          letterSpacing="-0.01em"
        >
          {titulo}
        </Heading>
      </Flex>
      <Box>{children}</Box>
    </GlassCard>
  );
}

/* ---------- Scroll-spy ---------- */

function useScrollSpy(ids: string[]) {
  const [ativo, setAtivo] = useState(ids[0] ?? '');
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => {
        const visivel = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visivel) setAtivo(visivel.target.id);
      },
      { rootMargin: '-90px 0px -65% 0px', threshold: 0 },
    );
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [ids]);
  return ativo;
}

/* ---------- Layout com TOC lateral (long-form) ---------- */

export interface TocItem {
  id: string;
  label: string;
}

export function TocLayout({ secoes, children }: { secoes: TocItem[]; children: React.ReactNode }) {
  const ids = useMemo(() => secoes.map(s => s.id), [secoes]);
  const ativo = useScrollSpy(ids);
  const irPara = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <Grid templateColumns={{ base: '1fr', lg: '220px minmax(0, 1fr)' }} gap={{ base: 6, lg: 10 }}>
      <Box display={{ base: 'none', lg: 'block' }}>
        <Box position="sticky" top="90px" alignSelf="start">
          <Text
            fontSize="10px"
            fontWeight="700"
            color="#94A3B8"
            textTransform="uppercase"
            mb={3}
            letterSpacing="0.08em"
          >
            Nesta página
          </Text>
          <Flex direction="column" gap={1}>
            {secoes.map(s => {
              const on = ativo === s.id;
              return (
                <Flex
                  key={s.id}
                  as="button"
                  align="center"
                  gap={2}
                  textAlign="left"
                  py={1}
                  onClick={() => irPara(s.id)}
                  aria-current={on ? 'location' : undefined}
                >
                  <Box
                    w="6px"
                    h="6px"
                    borderRadius="full"
                    flexShrink={0}
                    bg={on ? '#6366F1' : 'transparent'}
                    borderWidth={on ? '0' : '1px'}
                    borderColor="#CBD5E1"
                  />
                  <Text
                    fontSize="13px"
                    color={on ? '#0F172A' : '#64748B'}
                    fontWeight={on ? '600' : '500'}
                  >
                    {s.label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      </Box>
      <Box maxW="760px">{children}</Box>
    </Grid>
  );
}

/* ---------- Header da página (reusável entre abas não, só no index) ---------- */

export function copiarLinkSecao(id: string) {
  const url = `${window.location.origin}${window.location.pathname}#${id}`;
  navigator.clipboard?.writeText(url);
}

export const LinkSecaoBtn = ({ id }: { id: string }) => (
  <IconButton
    aria-label="Copiar link da seção"
    icon={<LinkSimple size={14} weight="bold" />}
    size="xs"
    variant="ghost"
    color="#CBD5E1"
    _hover={{ color: '#64748B' }}
    onClick={() => copiarLinkSecao(id)}
  />
);

/* Wrapper de entrada com motion (reuso nas abas). */
export function FadeIn({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
    >
      {children}
    </motion.div>
  );
}
